import { useEffect, useRef, useState } from "react";
import { useUser } from "../store";
import { shallow } from "zustand/shallow";
import { realtimeDatabase } from "../store/firebase";
import { onValue, serverTimestamp, ref, onDisconnect, off, update, DataSnapshot } from "firebase/database";

const useOnlineStatusV2 = (amIBlock: boolean) => {
  
  const [uid, currentUser] = useUser(
    (state) => [state.currentUser?.uid, state.currentUser],
    shallow
  );

  const connectedRef = ref(realtimeDatabase, ".info/connected");
  const userStatusDatabaseRef = ref(realtimeDatabase, "/status/" + uid);

  const isOfflineForDatabase = {
    state: "offline",
    last_changed: serverTimestamp(),
  };
  const isOnlineForDatabase = {
    state: "online",
    last_changed: serverTimestamp(),
  };

  const [online, setOnline] = useState<boolean>(true);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
  
    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("blur", handleOffline);
  
    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("blur", handleOffline);
    };
  }, []);

  useEffect(() => {

    if(!uid){
      return;
    }
    const userStatusDatabaseRef = ref(realtimeDatabase, "/status/" + uid);
    if(online){
      update(userStatusDatabaseRef, isOnlineForDatabase)
    }else{
      update(userStatusDatabaseRef, isOfflineForDatabase)
    }
    // eslint-disable-next-line
}, [uid, online])
  

  // eslint-disable-next-line
  const listenerAddedRef = useRef(false); // Ref to track if the listener has been added

  useEffect(() => {

    if(!uid){
      return;
    }

    const handleConnectionChange = (snapshot: DataSnapshot) => {

      if (snapshot.val() === false) {
        return;
      }

      onDisconnect(userStatusDatabaseRef).update(isOfflineForDatabase).then(() => {
        update(userStatusDatabaseRef, isOnlineForDatabase);
      });
    };

    if (!uid || !currentUser || amIBlock || currentUser?.isPrivate) {

      // Remove the listener if amIBlock becomes true or other conditions fail
      if (listenerAddedRef.current) {
        off(connectedRef, "value", handleConnectionChange);
        listenerAddedRef.current = false; // Reset listener tracking
      }
      
      update(userStatusDatabaseRef, isOfflineForDatabase)

      return;
    }


    onValue(connectedRef, handleConnectionChange);
    listenerAddedRef.current = true; 

    return () => {
      off(connectedRef, "value", handleConnectionChange);
      listenerAddedRef.current = false; 
    };
    // eslint-disable-next-line
  }, [uid]); 

  return null;
};

export { useOnlineStatusV2 };
